import {
  type ApprovalRequest,
  type AutomationPolicy,
  type CheckIn,
  type ContextFact,
  type DailyCapacity,
  type EnrichmentEvent,
  type ExecutionRun,
  type ExportData,
  type FieldEvidence,
  type Initiative,
  type KeyResult,
  type Objective,
  type PlanStep,
  type PlanningRun,
  type ProgressEvent,
  type ProtectedBlock,
  type ReminderPreferences,
  type ReviewReport,
  SCHEMA_VERSION,
  type ScheduleBlock,
  type SelectedTaskArrangement,
  type SmartList,
  type TaskActivity,
  type TaskArrangement,
  type TriageDisposition,
  type UiPreferences,
  type WorkflowColumn,
} from '@portfolio-pm/shared';
import * as Y from 'yjs';
import { listCheckIns } from './check-ins.ts';
import {
  getApprovalRequestsMap,
  getAutomationPoliciesMap,
  getCheckInsMap,
  getContextFactsMap,
  getDailyCapacitiesMap,
  getDeletedMap,
  getEnrichmentEventsMap,
  getExecutionReceiptsMap,
  getExecutionRunsMap,
  getFieldEvidenceMap,
  getInitiativesMap,
  getKeyResultsMap,
  getPlanStepsMap,
  getPlanningRunsMap,
  getProgressEventsMap,
  getProtectedBlocksMap,
  getReminderPreferencesMap,
  getReviewReportsMap,
  getScheduleBlocksMap,
  getSchemaVersion,
  getSelectedTaskArrangementsMap,
  getSmartListsMap,
  getTaskActivitiesMap,
  getTaskArrangementsMap,
  getTriageDispositionsMap,
  getUiPreferencesMap,
  getWorkflowColumnsMap,
} from './doc.ts';
import { listInitiatives } from './initiatives.ts';
import { listKeyResults, listObjectives } from './objectives.ts';
import {
  listDailyCapacities,
  listEnrichmentEvents,
  listFieldEvidence,
  listProgressEvents,
  listProtectedBlocks,
  listTriageDispositions,
} from './prioritization.ts';
import { listProjects } from './projects.ts';
import { getReminderPreferences } from './reminder-preferences.ts';
import { listSmartLists } from './smart-lists.ts';
import { listTaskActivities } from './task-activities.ts';
import { listSelectedTaskArrangements, listTaskArrangements } from './task-arrangements.ts';
import { listTasks } from './tasks.ts';
import { getUiPreferences } from './ui-preferences.ts';
import { listWorkflowColumns } from './workflow-columns.ts';
import {
  listApprovalRequests,
  listAutomationPolicies,
  listContextFacts,
  listExecutionReceipts,
  listExecutionRuns,
  listPlanSteps,
  listPlanningRuns,
  listReviewReports,
  listScheduleBlocks,
} from './workflow.ts';

// ─── Export ───────────────────────────────────────────────────────────────────

/**
 * Serialise the entire Y.Doc into a plain JSON-serialisable object.
 * The caller can then `JSON.stringify` it and offer it as a file download.
 */
export function exportDoc(doc: Y.Doc, now = new Date()): ExportData {
  return {
    schemaVersion: getSchemaVersion(doc) ?? SCHEMA_VERSION,
    exportedAt: now.toISOString(),
    projects: listProjects(doc),
    initiatives: listInitiatives(doc),
    reminderPreferences: getReminderPreferences(doc),
    uiPreferences: getUiPreferences(doc),
    objectives: listObjectives(doc),
    keyResults: listKeyResults(doc),
    checkIns: listCheckIns(doc),
    tasks: listTasks(doc),
    workflowColumns: listWorkflowColumns(doc),
    smartLists: listSmartLists(doc),
    taskArrangements: listTaskArrangements(doc),
    selectedTaskArrangements: listSelectedTaskArrangements(doc),
    taskActivities: listTaskActivities(doc),
    dailyCapacities: listDailyCapacities(doc),
    protectedBlocks: listProtectedBlocks(doc),
    fieldEvidence: listFieldEvidence(doc),
    progressEvents: listProgressEvents(doc),
    enrichmentEvents: listEnrichmentEvents(doc),
    triageDispositions: listTriageDispositions(doc),
    planningRuns: listPlanningRuns(doc),
    planSteps: listPlanSteps(doc),
    scheduleBlocks: listScheduleBlocks(doc),
    approvalRequests: listApprovalRequests(doc),
    automationPolicies: listAutomationPolicies(doc),
    executionRuns: listExecutionRuns(doc),
    executionReceipts: listExecutionReceipts(doc),
    contextFacts: listContextFacts(doc, true),
    reviewReports: listReviewReports(doc),
  };
}

// ─── Import ───────────────────────────────────────────────────────────────────

/**
 * Merge exported data into an existing Y.Doc.
 *
 * Strategy: last-write-wins per entity, keyed by id. Items that already exist
 * in the doc are updated only when the import's `updatedAt` is strictly newer
 * than the existing value — this preserves local edits that happened after an
 * export was taken. Items present in the `deleted` tombstone map are skipped
 * so re-importing an old export cannot resurrect soft-deleted records.
 *
 * All mutations are wrapped in a single transaction for atomicity.
 */
export function importData(doc: Y.Doc, data: ExportData): void {
  const deletedMap = getDeletedMap(doc);

  doc.transact(() => {
    // ── Projects ────────────────────────────────────────────────────────
    const projectsMap = doc.getMap<Y.Map<unknown>>('projects');
    for (const project of data.projects) {
      if (deletedMap.has(project.id)) continue; // tombstoned — skip
      const existing = projectsMap.get(project.id);
      if (existing) {
        // Overwrite only if the incoming data is newer.
        const existingUpdatedAt = Number(existing.get('updatedAt') ?? 0);
        if (project.updatedAt <= existingUpdatedAt) continue;
        existing.set('name', project.name);
        existing.set('archived', project.archived);
        existing.set('order', project.order);
        existing.set('updatedAt', project.updatedAt);
        if (project.areaId !== undefined) existing.set('areaId', project.areaId);
        else existing.delete('areaId');
        if (project.inbox) existing.set('inbox', true);
      } else {
        // Brand-new project.
        const inner = new Y.Map<unknown>();
        inner.set('name', project.name);
        inner.set('archived', project.archived);
        inner.set('order', project.order);
        inner.set('createdAt', project.createdAt);
        inner.set('updatedAt', project.updatedAt);
        if (project.areaId !== undefined) inner.set('areaId', project.areaId);
        if (project.inbox) inner.set('inbox', true);
        projectsMap.set(project.id, inner);
      }
    }

    // ── Objectives ──────────────────────────────────────────────────────
    const objectivesMap = doc.getMap<Y.Map<unknown>>('objectives');
    for (const objective of data.objectives ?? []) {
      if (deletedMap.has(objective.id)) continue;
      const existing = objectivesMap.get(objective.id);
      if (existing) {
        const existingUpdatedAt = Number(existing.get('updatedAt') ?? 0);
        if (objective.updatedAt <= existingUpdatedAt) continue;
        setObjectiveFields(existing, objective);
      } else {
        const inner = new Y.Map<unknown>();
        setObjectiveFields(inner, objective);
        objectivesMap.set(objective.id, inner);
      }
    }

    // ── Initiatives ─────────────────────────────────────────────────────
    for (const initiative of data.initiatives ?? []) {
      mergeTimestampedRecord(getInitiativesMap(doc), initiative, deletedMap, setInitiativeFields);
    }

    // ── Key Results ─────────────────────────────────────────────────────
    const keyResultsMap = getKeyResultsMap(doc);
    for (const keyResult of data.keyResults ?? []) {
      if (deletedMap.has(keyResult.id)) continue;
      const existing = keyResultsMap.get(keyResult.id);
      if (existing) {
        const existingUpdatedAt = Number(existing.get('updatedAt') ?? 0);
        if (keyResult.updatedAt <= existingUpdatedAt) continue;
        setKeyResultFields(existing, keyResult);
      } else {
        const inner = new Y.Map<unknown>();
        setKeyResultFields(inner, keyResult);
        keyResultsMap.set(keyResult.id, inner);
      }
    }

    // ── Check-ins ───────────────────────────────────────────────────────
    const checkInsMap = getCheckInsMap(doc);
    for (const checkIn of data.checkIns ?? []) {
      if (deletedMap.has(checkIn.id)) continue;
      // Check-ins are immutable; if id exists, keep existing and skip.
      if (checkInsMap.has(checkIn.id)) continue;
      const inner = new Y.Map<unknown>();
      setCheckInFields(inner, checkIn);
      checkInsMap.set(checkIn.id, inner);
    }

    // ── Reminder Preferences ────────────────────────────────────────────
    if (data.reminderPreferences) {
      const reminderPreferencesMap = getReminderPreferencesMap(doc);
      const existingUpdatedAt = Number(reminderPreferencesMap.get('updatedAt') ?? 0);
      if (data.reminderPreferences.updatedAt > existingUpdatedAt) {
        setReminderPreferencesFields(reminderPreferencesMap, data.reminderPreferences);
      }
    }

    // ── UI Preferences ─────────────────────────────────────────────────
    if (data.uiPreferences) {
      const uiPreferencesMap = getUiPreferencesMap(doc);
      const existingUpdatedAt = Number(uiPreferencesMap.get('updatedAt') ?? 0);
      if (data.uiPreferences.updatedAt > existingUpdatedAt) {
        setUiPreferencesFields(uiPreferencesMap, data.uiPreferences);
      }
    }

    // ── Workflow Columns ────────────────────────────────────────────────
    const workflowColumnsMap = getWorkflowColumnsMap(doc);
    for (const column of data.workflowColumns ?? []) {
      if (deletedMap.has(column.id)) continue;
      const existing = workflowColumnsMap.get(column.id);
      if (existing) {
        const existingUpdatedAt = Number(existing.get('updatedAt') ?? 0);
        if (column.updatedAt <= existingUpdatedAt) continue;
        setWorkflowColumnFields(existing, column);
      } else {
        const inner = new Y.Map<unknown>();
        setWorkflowColumnFields(inner, column);
        workflowColumnsMap.set(column.id, inner);
      }
    }

    // ── Tasks ───────────────────────────────────────────────────────────
    const tasksMap = doc.getMap<Y.Map<unknown>>('tasks');
    for (const task of data.tasks) {
      if (deletedMap.has(task.id)) continue; // tombstoned — skip
      const existing = tasksMap.get(task.id);
      if (existing) {
        const existingUpdatedAt = Number(existing.get('updatedAt') ?? 0);
        if (task.updatedAt <= existingUpdatedAt) continue;
        setTaskFields(existing, task);
      } else {
        const inner = new Y.Map<unknown>();
        setTaskFields(inner, task);
        tasksMap.set(task.id, inner);
      }
    }

    // ── SmartLists ──────────────────────────────────────────────────────
    const smartListsMap = getSmartListsMap(doc);
    for (const sl of data.smartLists ?? []) {
      if (deletedMap.has(sl.id)) continue;
      const existing = smartListsMap.get(sl.id);
      if (existing) {
        const existingUpdatedAt = Number(existing.get('updatedAt') ?? 0);
        if (sl.updatedAt <= existingUpdatedAt) continue;
        setSmartListFields(existing, sl);
      } else {
        const inner = new Y.Map<unknown>();
        setSmartListFields(inner, sl);
        smartListsMap.set(sl.id, inner);
      }
    }

    // ── Task Arrangements ───────────────────────────────────────────────
    const taskArrangementsMap = getTaskArrangementsMap(doc);
    for (const arrangement of data.taskArrangements ?? []) {
      if (deletedMap.has(arrangement.id)) continue;
      const existing = taskArrangementsMap.get(arrangement.id);
      if (existing) {
        const existingUpdatedAt = Number(existing.get('updatedAt') ?? 0);
        if (arrangement.updatedAt <= existingUpdatedAt) continue;
        setTaskArrangementFields(existing, arrangement);
      } else {
        const inner = new Y.Map<unknown>();
        setTaskArrangementFields(inner, arrangement);
        taskArrangementsMap.set(arrangement.id, inner);
      }
    }

    // ── Selected Task Arrangements ──────────────────────────────────────
    const selectedTaskArrangementsMap = getSelectedTaskArrangementsMap(doc);
    for (const selected of data.selectedTaskArrangements ?? []) {
      const existing = selectedTaskArrangementsMap.get(selected.listKey);
      if (existing) {
        const existingUpdatedAt = Number(existing.get('updatedAt') ?? 0);
        if (selected.updatedAt <= existingUpdatedAt) continue;
        setSelectedTaskArrangementFields(existing, selected);
      } else {
        const inner = new Y.Map<unknown>();
        setSelectedTaskArrangementFields(inner, selected);
        selectedTaskArrangementsMap.set(selected.listKey, inner);
      }
    }

    // ── Task Activities ─────────────────────────────────────────────────
    const taskActivitiesMap = getTaskActivitiesMap(doc);
    for (const activity of data.taskActivities ?? []) {
      if (deletedMap.has(activity.id)) continue;
      if (taskActivitiesMap.has(activity.id)) continue;
      const inner = new Y.Map<unknown>();
      setTaskActivityFields(inner, activity);
      taskActivitiesMap.set(activity.id, inner);
    }

    for (const capacity of data.dailyCapacities ?? []) {
      mergeTimestampedRecord(
        getDailyCapacitiesMap(doc),
        capacity,
        deletedMap,
        setDailyCapacityFields,
      );
    }
    for (const block of data.protectedBlocks ?? []) {
      mergeTimestampedRecord(
        getProtectedBlocksMap(doc),
        block,
        deletedMap,
        setProtectedBlockFields,
      );
    }
    for (const evidence of data.fieldEvidence ?? []) {
      mergeTimestampedRecord(
        getFieldEvidenceMap(doc),
        evidence,
        deletedMap,
        setFieldEvidenceFields,
        'recordedAt',
      );
    }
    for (const progress of data.progressEvents ?? []) {
      mergeImmutableRecord(getProgressEventsMap(doc), progress, deletedMap, setProgressEventFields);
    }
    for (const event of data.enrichmentEvents ?? []) {
      mergeImmutableRecord(
        getEnrichmentEventsMap(doc),
        event,
        deletedMap,
        setEnrichmentEventFields,
      );
    }
    for (const disposition of data.triageDispositions ?? []) {
      mergeTimestampedRecord(
        getTriageDispositionsMap(doc),
        disposition,
        deletedMap,
        setTriageDispositionFields,
      );
    }
    mergeWorkflowRecords(getPlanningRunsMap(doc), data.planningRuns ?? [], deletedMap);
    mergeWorkflowRecords(getPlanStepsMap(doc), data.planSteps ?? [], deletedMap);
    mergeWorkflowRecords(getScheduleBlocksMap(doc), data.scheduleBlocks ?? [], deletedMap);
    mergeWorkflowRecords(getApprovalRequestsMap(doc), data.approvalRequests ?? [], deletedMap);
    mergeWorkflowRecords(getAutomationPoliciesMap(doc), data.automationPolicies ?? [], deletedMap);
    mergeWorkflowRecords(getExecutionRunsMap(doc), data.executionRuns ?? [], deletedMap);
    for (const receipt of data.executionReceipts ?? []) {
      mergeImmutableRecord(getExecutionReceiptsMap(doc), receipt, deletedMap, setPlainRecordFields);
    }
    mergeWorkflowRecords(getContextFactsMap(doc), data.contextFacts ?? [], deletedMap);
    mergeWorkflowRecords(getReviewReportsMap(doc), data.reviewReports ?? [], deletedMap);
  });
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

import type { Task } from '@portfolio-pm/shared';

function setTaskFields(inner: Y.Map<unknown>, task: Task): void {
  inner.set('title', task.title);
  inner.set('projectId', task.projectId);
  inner.set('workflowState', task.workflowState ?? 'backlog');
  inner.set('priority', task.priority);
  inner.set('tags', [...task.tags]);
  inner.set('createdAt', task.createdAt);
  inner.set('updatedAt', task.updatedAt);

  setOrDelete(inner, 'notes', task.notes);
  setOrDelete(inner, 'parentTaskId', task.parentTaskId);
  setOrDelete(inner, 'krId', task.krId);
  setOrDelete(inner, 'objectiveId', task.objectiveId);
  setOrDelete(inner, 'initiativeId', task.initiativeId);
  setOrDelete(
    inner,
    'planningContextIds',
    task.planningContextIds ? [...task.planningContextIds] : undefined,
  );
  setOrDelete(inner, 'commitmentState', task.commitmentState);
  setOrDelete(inner, 'actionability', task.actionability);
  setOrDelete(inner, 'expectedOutcome', task.expectedOutcome);
  setOrDelete(inner, 'availabilityTrigger', task.availabilityTrigger);
  setOrDelete(inner, 'waitingReason', task.waitingReason);
  setOrDelete(inner, 'reviewAt', task.reviewAt);
  setOrDelete(inner, 'dueAt', task.dueAt);
  setOrDelete(
    inner,
    'dueAtHasTime',
    task.dueAt === undefined ? undefined : (task.dueAtHasTime ?? true),
  );
  setOrDelete(inner, 'scheduledAt', task.scheduledAt);
  setOrDelete(
    inner,
    'scheduledAtHasTime',
    task.scheduledAt === undefined ? undefined : (task.scheduledAtHasTime ?? true),
  );
  setOrDelete(inner, 'reminderAt', task.reminderAt);
  setOrDelete(inner, 'estimateMinutes', task.estimateMinutes);
  setOrDelete(inner, 'energy', task.energy);
  setOrDelete(inner, 'daypart', task.daypart);
  setOrDelete(inner, 'location', task.location);
  setOrDelete(inner, 'owner', task.owner);
  setOrDelete(inner, 'url', task.url);
  setOrDelete(inner, 'postponed', task.postponed);
  setOrDelete(inner, 'source', task.source);
  setOrDelete(inner, 'favorite', task.favorite);
  setOrDelete(inner, 'completedAt', task.completedAt);
  setOrDelete(inner, 'trashedAt', task.trashedAt);
  setOrDelete(inner, 'dependsOn', task.dependsOn ? [...task.dependsOn] : undefined);
  setOrDelete(inner, 'recurrence', task.recurrence ? { ...task.recurrence } : undefined);
  setOrDelete(inner, 'manualOrder', task.manualOrder);
}

function setWorkflowColumnFields(inner: Y.Map<unknown>, column: WorkflowColumn): void {
  inner.set('name', column.name);
  inner.set('order', column.order);
  inner.set('createdAt', column.createdAt);
  inner.set('updatedAt', column.updatedAt);
  setOrDelete(inner, 'protected', column.protected);
}

function setSmartListFields(inner: Y.Map<unknown>, smartList: SmartList): void {
  inner.set('name', smartList.name);
  inner.set('query', smartList.query);
  setOrDelete(inner, 'ruleTree', smartList.ruleTree);
  inner.set('groupBy', smartList.groupBy);
  inner.set('groupDirection', smartList.groupDirection);
  inner.set(
    'sort',
    smartList.sort.map((sort) => ({ ...sort })),
  );
  inner.set('showCompleted', smartList.showCompleted);
  inner.set('showTrashed', smartList.showTrashed);
  inner.set('order', smartList.order);
  inner.set('createdAt', smartList.createdAt);
  inner.set('updatedAt', smartList.updatedAt);
}

function setTaskArrangementFields(inner: Y.Map<unknown>, arrangement: TaskArrangement): void {
  inner.set('name', arrangement.name);
  inner.set('groupBy', arrangement.groupBy);
  inner.set('groupDirection', arrangement.groupDirection);
  inner.set(
    'sort',
    arrangement.sort.map((sort) => ({ ...sort })),
  );
  inner.set('order', arrangement.order);
  inner.set('createdAt', arrangement.createdAt);
  inner.set('updatedAt', arrangement.updatedAt);
}

function setSelectedTaskArrangementFields(
  inner: Y.Map<unknown>,
  selected: SelectedTaskArrangement,
): void {
  inner.set('arrangementId', selected.arrangementId);
  inner.set('subtaskDisplay', selected.subtaskDisplay);
  inner.set('createdAt', selected.createdAt);
  inner.set('updatedAt', selected.updatedAt);
}

function setTaskActivityFields(inner: Y.Map<unknown>, activity: TaskActivity): void {
  inner.set('taskId', activity.taskId);
  inner.set('type', activity.type);
  inner.set('message', activity.message);
  inner.set('createdAt', activity.createdAt);
}

function setReminderPreferencesFields(
  inner: Y.Map<unknown>,
  reminderPreferences: ReminderPreferences,
): void {
  inner.set('dueNudgesEnabled', reminderPreferences.dueNudgesEnabled);
  inner.set('dueLeadMinutes', reminderPreferences.dueLeadMinutes);
  inner.set('scheduledNudgesEnabled', reminderPreferences.scheduledNudgesEnabled);
  inner.set('scheduledLeadMinutes', reminderPreferences.scheduledLeadMinutes);
  inner.set('createdAt', reminderPreferences.createdAt);
  inner.set('updatedAt', reminderPreferences.updatedAt);
}

function setUiPreferencesFields(inner: Y.Map<unknown>, uiPreferences: UiPreferences): void {
  inner.set('priorityColors', { ...uiPreferences.priorityColors });
  inner.set('tagColors', { ...uiPreferences.tagColors });
  inner.set('taskSwipeLeftToRight', uiPreferences.taskSwipeLeftToRight);
  inner.set('taskSwipeRightToLeft', uiPreferences.taskSwipeRightToLeft);
  inner.set(
    'favoriteShortcuts',
    uiPreferences.favoriteShortcuts.map((shortcut) => ({ ...shortcut })),
  );
  inner.set('projectSidebarSort', uiPreferences.projectSidebarSort);
  inner.set('projectSidebarCount', uiPreferences.projectSidebarCount);
  inner.set('projectSidebarShowArchived', uiPreferences.projectSidebarShowArchived);
  inner.set('createdAt', uiPreferences.createdAt);
  inner.set('updatedAt', uiPreferences.updatedAt);
}

function setObjectiveFields(inner: Y.Map<unknown>, objective: Objective): void {
  inner.set('projectId', objective.projectId);
  inner.set('title', objective.title);
  inner.set('status', objective.status);
  inner.set('order', objective.order);
  inner.set('createdAt', objective.createdAt);
  inner.set('updatedAt', objective.updatedAt);

  setOrDelete(inner, 'parentObjectiveId', objective.parentObjectiveId);
  setOrDelete(inner, 'description', objective.description);
  setOrDelete(inner, 'startDate', objective.startDate);
  setOrDelete(inner, 'endDate', objective.endDate);
  setOrDelete(inner, 'outcome', objective.outcome);
  setOrDelete(inner, 'commitmentState', objective.commitmentState);
  setOrDelete(inner, 'importance', objective.importance);
  setOrDelete(inner, 'reviewAt', objective.reviewAt);
  setOrDelete(inner, 'minimumProgressCadenceDays', objective.minimumProgressCadenceDays);
}

function setInitiativeFields(inner: Y.Map<unknown>, initiative: Initiative): void {
  inner.set('projectId', initiative.projectId);
  inner.set('title', initiative.title);
  inner.set('lifecycle', initiative.lifecycle);
  inner.set('objectiveIds', [...initiative.objectiveIds]);
  inner.set('createdAt', initiative.createdAt);
  inner.set('updatedAt', initiative.updatedAt);
  setOrDelete(inner, 'description', initiative.description);
  setOrDelete(inner, 'successDefinition', initiative.successDefinition);
  setOrDelete(inner, 'commitmentState', initiative.commitmentState);
  setOrDelete(inner, 'importance', initiative.importance);
  setOrDelete(inner, 'reviewAt', initiative.reviewAt);
  setOrDelete(inner, 'reviewTrigger', initiative.reviewTrigger);
  setOrDelete(inner, 'minimumProgressCadenceDays', initiative.minimumProgressCadenceDays);
}

function setDailyCapacityFields(inner: Y.Map<unknown>, capacity: DailyCapacity): void {
  inner.set('localDate', capacity.localDate);
  inner.set('timezone', capacity.timezone);
  inner.set('availableMinutes', capacity.availableMinutes);
  inner.set('createdAt', capacity.createdAt);
  inner.set('updatedAt', capacity.updatedAt);
}

function setProtectedBlockFields(inner: Y.Map<unknown>, block: ProtectedBlock): void {
  inner.set('localDate', block.localDate);
  inner.set('timezone', block.timezone);
  inner.set('durationMinutes', block.durationMinutes);
  inner.set('createdAt', block.createdAt);
  inner.set('updatedAt', block.updatedAt);
  setOrDelete(inner, 'startAt', block.startAt);
  setOrDelete(inner, 'endAt', block.endAt);
  setOrDelete(inner, 'objectiveId', block.objectiveId);
  setOrDelete(inner, 'initiativeId', block.initiativeId);
  setOrDelete(inner, 'note', block.note);
}

function setFieldEvidenceFields(inner: Y.Map<unknown>, evidence: FieldEvidence): void {
  inner.set('entityType', evidence.entityType);
  inner.set('entityId', evidence.entityId);
  inner.set('field', evidence.field);
  inner.set('valueState', evidence.valueState);
  inner.set('source', evidence.source);
  inner.set('actor', evidence.actor);
  inner.set('recordedAt', evidence.recordedAt);
  setOrDelete(inner, 'tool', evidence.tool);
  setOrDelete(inner, 'valueHash', evidence.valueHash);
  setOrDelete(inner, 'confirmationId', evidence.confirmationId);
  setOrDelete(inner, 'proposalFingerprint', evidence.proposalFingerprint);
}

function setProgressEventFields(inner: Y.Map<unknown>, event: ProgressEvent): void {
  inner.set('entityType', event.entityType);
  inner.set('entityId', event.entityId);
  inner.set('occurredAt', event.occurredAt);
  inner.set('source', event.source);
  inner.set('actor', event.actor);
  inner.set('createdAt', event.createdAt);
  setOrDelete(inner, 'note', event.note);
  setOrDelete(inner, 'tool', event.tool);
}

function setEnrichmentEventFields(inner: Y.Map<unknown>, event: EnrichmentEvent): void {
  inner.set('entityType', event.entityType);
  inner.set('entityId', event.entityId);
  inner.set('action', event.action);
  inner.set(
    'changes',
    event.changes.map((change) => ({ ...change })),
  );
  inner.set('actor', event.actor);
  inner.set('createdAt', event.createdAt);
  setOrDelete(inner, 'tool', event.tool);
  setOrDelete(inner, 'confirmationId', event.confirmationId);
  setOrDelete(inner, 'proposalFingerprint', event.proposalFingerprint);
}

function setTriageDispositionFields(inner: Y.Map<unknown>, disposition: TriageDisposition): void {
  inner.set('entityType', disposition.entityType);
  inner.set('entityId', disposition.entityId);
  inner.set('disposition', disposition.disposition);
  inner.set('reasonCodes', [...disposition.reasonCodes]);
  inner.set('sourceFingerprint', disposition.sourceFingerprint);
  inner.set('actor', disposition.actor);
  inner.set('createdAt', disposition.createdAt);
  inner.set('updatedAt', disposition.updatedAt);
  setOrDelete(inner, 'resurfaceAt', disposition.resurfaceAt);
  setOrDelete(inner, 'tool', disposition.tool);
}

function mergeTimestampedRecord<T extends { id: string }>(
  map: Y.Map<Y.Map<unknown>>,
  record: T,
  deletedMap: Y.Map<number>,
  setFields: (inner: Y.Map<unknown>, record: T) => void,
  timestampField = 'updatedAt',
): void {
  if (deletedMap.has(record.id)) return;
  const incomingTimestamp = Number((record as Record<string, unknown>)[timestampField] ?? 0);
  const existing = map.get(record.id);
  if (existing && incomingTimestamp <= Number(existing.get(timestampField) ?? 0)) return;
  const inner = existing ?? new Y.Map<unknown>();
  setFields(inner, record);
  if (!existing) map.set(record.id, inner);
}

function mergeImmutableRecord<T extends { id: string }>(
  map: Y.Map<Y.Map<unknown>>,
  record: T,
  deletedMap: Y.Map<number>,
  setFields: (inner: Y.Map<unknown>, record: T) => void,
): void {
  if (deletedMap.has(record.id) || map.has(record.id)) return;
  const inner = new Y.Map<unknown>();
  setFields(inner, record);
  map.set(record.id, inner);
}

type WorkflowRecord =
  | PlanningRun
  | PlanStep
  | ScheduleBlock
  | ApprovalRequest
  | AutomationPolicy
  | ExecutionRun
  | ContextFact
  | ReviewReport;

function mergeWorkflowRecords<T extends WorkflowRecord>(
  map: Y.Map<Y.Map<unknown>>,
  records: T[],
  deletedMap: Y.Map<number>,
): void {
  for (const record of records) {
    mergeTimestampedRecord(map, record, deletedMap, setPlainRecordFields);
  }
}

function setPlainRecordFields<T extends { id: string }>(inner: Y.Map<unknown>, record: T): void {
  const values = record as Record<string, unknown>;
  if (inner.doc) {
    for (const key of [...inner.keys()]) {
      if (key !== 'id' && values[key] === undefined) inner.delete(key);
    }
  }
  for (const [key, value] of Object.entries(values)) {
    if (key === 'id' || value === undefined) continue;
    inner.set(key, structuredClone(value));
  }
}

function setKeyResultFields(inner: Y.Map<unknown>, keyResult: KeyResult): void {
  inner.set('objectiveId', keyResult.objectiveId);
  inner.set('title', keyResult.title);
  inner.set('progressMode', keyResult.progressMode);
  inner.set('order', keyResult.order);
  inner.set('createdAt', keyResult.createdAt);
  inner.set('updatedAt', keyResult.updatedAt);

  setOrDelete(inner, 'description', keyResult.description);
  setOrDelete(inner, 'manualProgress', keyResult.manualProgress);
  setOrDelete(inner, 'startValue', keyResult.startValue);
  setOrDelete(inner, 'currentValue', keyResult.currentValue);
  setOrDelete(inner, 'targetValue', keyResult.targetValue);
  setOrDelete(inner, 'status', keyResult.status);
  setOrDelete(inner, 'confidence', keyResult.confidence);
}

function setCheckInFields(inner: Y.Map<unknown>, checkIn: CheckIn): void {
  inner.set('cadence', checkIn.cadence);
  inner.set('targetType', checkIn.targetType);
  inner.set('targetId', checkIn.targetId);
  inner.set('note', checkIn.note);
  inner.set('createdAt', checkIn.createdAt);

  setOrDelete(inner, 'blockers', checkIn.blockers);
  setOrDelete(inner, 'nextSteps', checkIn.nextSteps);
  setOrDelete(inner, 'statusUpdate', checkIn.statusUpdate);
  setOrDelete(inner, 'confidenceUpdate', checkIn.confidenceUpdate);
  setOrDelete(inner, 'progressSnapshot', checkIn.progressSnapshot);
}

function setOrDelete(inner: Y.Map<unknown>, key: string, value: unknown): void {
  if (value === undefined) {
    inner.delete(key);
  } else {
    inner.set(key, value);
  }
}
